/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * 자산 상세 Mock Store
 * Table, Database, Schema 등의 상세 정보를 목업으로 제공합니다.
 */
import { ALL_ICEBERG_DATA } from '@/shared/api/demo/data/icebergData';

import type { AssetBaseInfo } from '@/features/asset-detail/api/baseApi';

type MockAssetDetail = AssetBaseInfo;

const MOCK_TABLES = {
  'postgres.production.public.users': {
    id: 'table-1',
    name: 'users',
    displayName: 'Users',
    fullyQualifiedName: 'postgres.production.public.users',
    description: '사용자 기본 정보를 저장하는 핵심 테이블입니다. 회원가입, 로그인, 프로필 관리 등에 사용됩니다.',
    tags: [
      {
        tagFQN: 'Tier.Bronze',
        name: 'Bronze',
        displayName: 'Bronze',
        description: '원천 데이터 레이어',
        labelType: 'Manual',
        source: 'Classification',
        state: 'Confirmed',
      },
      {
        tagFQN: 'Classification.PII',
        name: 'PII',
        displayName: 'PII',
        description: '개인정보 포함',
        labelType: 'Automated',
        source: 'Classification',
        state: 'Confirmed',
      },
    ],
    owners: [
      {
        id: 'owner-1',
        name: 'data-platform',
        displayName: 'Data Platform Team',
        type: 'team',
      },
    ],
    service: {
      id: 'svc-postgres',
      type: 'databaseService',
      name: 'postgres',
      displayName: 'Postgres',
    },
    database: {
      id: 'db-production',
      type: 'database',
      name: 'production',
      displayName: 'Production',
      fullyQualifiedName: 'postgres.production',
    },
    databaseSchema: {
      id: 'schema-public',
      type: 'databaseSchema',
      name: 'public',
      displayName: 'Public',
    },
    tableType: 'Regular',
    columns: [
      {
        name: 'id',
        dataType: 'BIGINT',
        dataTypeDisplay: 'bigint',
        description: '사용자 고유 식별자',
        fullyQualifiedName: 'postgres.production.public.users.id',
        tags: [],
        constraint: 'PRIMARY_KEY',
        ordinalPosition: 1,
      },
      {
        name: 'email',
        dataType: 'VARCHAR',
        dataTypeDisplay: 'varchar(255)',
        description: '사용자 이메일 주소',
        fullyQualifiedName: 'postgres.production.public.users.email',
        tags: [
          {
            tagFQN: 'Classification.PII',
            name: 'PII',
            labelType: 'Automated',
            source: 'Classification',
            state: 'Confirmed',
          },
        ],
        constraint: 'UNIQUE',
        ordinalPosition: 2,
      },
      {
        name: 'name',
        dataType: 'VARCHAR',
        dataTypeDisplay: 'varchar(100)',
        description: '사용자 이름',
        fullyQualifiedName: 'postgres.production.public.users.name',
        tags: [],
        ordinalPosition: 3,
      },
      {
        name: 'created_at',
        dataType: 'TIMESTAMP',
        dataTypeDisplay: 'timestamp',
        description: '생성 시각',
        fullyQualifiedName: 'postgres.production.public.users.created_at',
        tags: [],
        ordinalPosition: 4,
      },
    ] as any,
    usageSummary: {
      date: new Date().toISOString(),
      weeklyStats: {
        count: 1250,
        percentileRank: 85.5,
      } as any,
      monthlyStats: {
        count: 5400,
        percentileRank: 82.3,
      } as any,
      dailyStats: {
        count: 180,
        percentileRank: 78.9,
      } as any,
    },
    version: 1.2,
    updatedAt: Date.now() - 86400000,
    updatedBy: 'admin',
    changeDescription: {
      fieldsAdded: [],
      fieldsUpdated: [],
      fieldsDeleted: [],
      previousVersion: 1.1,
    },
  },
  'postgres.production.public.orders': {
    id: 'table-2',
    name: 'orders',
    displayName: 'Orders',
    fullyQualifiedName: 'postgres.production.public.orders',
    description: '주문 정보를 저장하는 테이블입니다. 주문 생성, 상태 관리, 결제 연동 등에 사용됩니다.',
    tags: [
      {
        tagFQN: 'Tier.Bronze',
        name: 'Bronze',
        displayName: 'Bronze',
        labelType: 'Manual',
        source: 'Classification',
        state: 'Confirmed',
      },
    ],
    owners: [
      {
        id: 'owner-2',
        name: 'commerce-team',
        displayName: 'Commerce Team',
        type: 'team',
      },
    ],
    service: {
      id: 'svc-postgres',
      type: 'databaseService',
      name: 'postgres',
      displayName: 'Postgres',
    },
    database: {
      id: 'db-production',
      type: 'database',
      name: 'production',
      displayName: 'Production',
      fullyQualifiedName: 'postgres.production',
    },
    databaseSchema: {
      id: 'schema-public',
      type: 'databaseSchema',
      name: 'public',
      displayName: 'Public',
    },
    tableType: 'Regular',
    columns: [
      {
        name: 'id',
        dataType: 'BIGINT',
        dataTypeDisplay: 'bigint',
        description: '주문 고유 식별자',
        fullyQualifiedName: 'postgres.production.public.orders.id',
        tags: [],
        constraint: 'PRIMARY_KEY',
        ordinalPosition: 1,
      },
      {
        name: 'user_id',
        dataType: 'BIGINT',
        dataTypeDisplay: 'bigint',
        description: '주문한 사용자 ID',
        fullyQualifiedName: 'postgres.production.public.orders.user_id',
        tags: [],
        constraint: 'FOREIGN_KEY',
        ordinalPosition: 2,
      },
      {
        name: 'total_amount',
        dataType: 'DECIMAL',
        dataTypeDisplay: 'decimal(10,2)',
        description: '주문 총액',
        fullyQualifiedName: 'postgres.production.public.orders.total_amount',
        tags: [],
        ordinalPosition: 3,
      },
      {
        name: 'status',
        dataType: 'VARCHAR',
        dataTypeDisplay: 'varchar(50)',
        description: '주문 상태',
        fullyQualifiedName: 'postgres.production.public.orders.status',
        tags: [],
        ordinalPosition: 4,
      },
      {
        name: 'created_at',
        dataType: 'TIMESTAMP',
        dataTypeDisplay: 'timestamp',
        description: '주문 생성 시각',
        fullyQualifiedName: 'postgres.production.public.orders.created_at',
        tags: [],
        ordinalPosition: 5,
      },
    ],
    usageSummary: {
      date: new Date().toISOString(),
      weeklyStats: { count: 980, percentileRank: 72.1 },
      monthlyStats: { count: 4200, percentileRank: 69.8 },
      dailyStats: { count: 140, percentileRank: 68.3 },
    },
    version: 1.0,
    updatedAt: Date.now() - 172800000,
    updatedBy: 'admin',
  },
  'postgres.analytics.public.customer_360': {
    id: 'table-4',
    name: 'customer_360',
    displayName: 'Customer 360',
    fullyQualifiedName: 'postgres.analytics.public.customer_360',
    description:
      '고객의 360도 통합 뷰를 제공하는 분석용 테이블입니다. 다양한 소스에서 수집한 고객 데이터를 통합합니다.',
    tags: [
      {
        tagFQN: 'Tier.Gold',
        name: 'Gold',
        displayName: 'Gold',
        description: '분석 준비 데이터 레이어',
        labelType: 'Manual',
        source: 'Classification',
        state: 'Confirmed',
      },
    ],
    owners: [
      {
        id: 'owner-3',
        name: 'analytics-team',
        displayName: 'Analytics Team',
        type: 'team',
      },
    ],
    service: {
      id: 'svc-postgres',
      type: 'databaseService',
      name: 'postgres',
      displayName: 'Postgres',
    },
    database: {
      id: 'db-analytics',
      type: 'database',
      name: 'analytics',
      displayName: 'Analytics',
      fullyQualifiedName: 'postgres.analytics',
    },
    databaseSchema: {
      id: 'schema-analytics-public',
      type: 'databaseSchema',
      name: 'public',
      displayName: 'Public',
    },
    tableType: 'Regular',
    columns: [
      {
        name: 'customer_id',
        dataType: 'BIGINT',
        dataTypeDisplay: 'bigint',
        description: '고객 고유 식별자',
        fullyQualifiedName: 'postgres.analytics.public.customer_360.customer_id',
        tags: [],
        constraint: 'PRIMARY_KEY',
        ordinalPosition: 1,
      },
      {
        name: 'total_purchases',
        dataType: 'INTEGER',
        dataTypeDisplay: 'integer',
        description: '총 구매 횟수',
        fullyQualifiedName: 'postgres.analytics.public.customer_360.total_purchases',
        tags: [],
        ordinalPosition: 2,
      },
      {
        name: 'lifetime_value',
        dataType: 'DECIMAL',
        dataTypeDisplay: 'decimal(12,2)',
        description: '고객 생애 가치',
        fullyQualifiedName: 'postgres.analytics.public.customer_360.lifetime_value',
        tags: [],
        ordinalPosition: 3,
      },
      {
        name: 'segment',
        dataType: 'VARCHAR',
        dataTypeDisplay: 'varchar(50)',
        description: '고객 세그먼트',
        fullyQualifiedName: 'postgres.analytics.public.customer_360.segment',
        tags: [],
        ordinalPosition: 4,
      },
    ],
    usageSummary: {
      date: new Date().toISOString(),
      weeklyStats: { count: 2100, percentileRank: 92.5 },
      monthlyStats: { count: 8900, percentileRank: 91.2 },
      dailyStats: { count: 300, percentileRank: 89.7 },
    },
    version: 2.1,
    updatedAt: Date.now() - 43200000,
    updatedBy: 'analytics-team',
  },
} as unknown as Record<string, MockAssetDetail>;

const MOCK_DATABASES = {
  'postgres.production': {
    id: 'db-production',
    name: 'production',
    displayName: 'Production',
    fullyQualifiedName: 'postgres.production',
    description: '프로덕션 환경의 주요 데이터베이스입니다. 실시간 트랜잭션 데이터를 저장합니다.',
    tags: [],
    owners: [
      {
        id: 'owner-1',
        name: 'data-platform',
        displayName: 'Data Platform Team',
        type: 'team',
      },
    ],
    service: {
      id: 'svc-postgres',
      type: 'databaseService',
      name: 'postgres',
      displayName: 'Postgres',
    },
    databaseSchemas: [
      {
        deleted: false,
        displayName: 'Public',
        name: 'public',
        id: 'schema-public',
        type: 'databaseSchema',
        fullyQualifiedName: 'postgres.production.public',
        description: '공용 스키마',
      },
    ],
    version: 1.0,
    updatedAt: Date.now() - 86400000,
    updatedBy: 'admin',
  },
  'postgres.analytics': {
    id: 'db-analytics',
    name: 'analytics',
    displayName: 'Analytics',
    fullyQualifiedName: 'postgres.analytics',
    description: '분석용 데이터베이스입니다. 집계 및 리포팅에 사용됩니다.',
    tags: [],
    owners: [
      {
        id: 'owner-3',
        name: 'analytics-team',
        displayName: 'Analytics Team',
        type: 'team',
      },
    ],
    service: {
      id: 'svc-postgres',
      type: 'databaseService',
      name: 'postgres',
      displayName: 'Postgres',
    },
    databaseSchemas: [
      {
        deleted: false,
        displayName: 'Public',
        name: 'public',
        id: 'schema-analytics-public',
        type: 'databaseSchema',
        fullyQualifiedName: 'postgres.analytics.public',
        description: '공용 스키마',
      },
    ],
    version: 1.0,
    updatedAt: Date.now() - 172800000,
    updatedBy: 'admin',
  },
} as unknown as Record<string, MockAssetDetail>;

const MOCK_SCHEMAS = {
  'postgres.production.public': {
    id: 'schema-public',
    name: 'public',
    displayName: 'Public',
    fullyQualifiedName: 'postgres.production.public',
    description: 'PostgreSQL의 기본 공용 스키마입니다.',
    tags: [],
    owners: [],
    service: {
      id: 'svc-postgres',
      type: 'databaseService',
      name: 'postgres',
      displayName: 'Postgres',
    },
    database: {
      id: 'db-production',
      type: 'database',
      name: 'production',
      displayName: 'Production',
      fullyQualifiedName: 'postgres.production',
    },
    tables: [
      {
        deleted: false,
        displayName: 'Users',
        name: 'users',
        id: 'table-1',
        type: 'table',
        fullyQualifiedName: 'postgres.production.public.users',
      },
      {
        deleted: false,
        displayName: 'Orders',
        name: 'orders',
        id: 'table-2',
        type: 'table',
        fullyQualifiedName: 'postgres.production.public.orders',
      },
      {
        deleted: false,
        displayName: 'Products',
        name: 'products',
        id: 'table-3',
        type: 'table',
        fullyQualifiedName: 'postgres.production.public.products',
      },
    ],
    version: 1.0,
    updatedAt: Date.now() - 86400000,
    updatedBy: 'system',
  },
} as unknown as Record<string, MockAssetDetail>;

const MOCK_CONTAINERS = {
  's3.raw_data': {
    id: 'container-1',
    name: 'raw_data',
    displayName: 'Raw Data',
    fullyQualifiedName: 's3.raw_data',
    description: 'S3 원천 데이터 저장소입니다. 다양한 소스에서 수집한 원시 데이터를 저장합니다.',
    tags: [],
    owners: [
      {
        id: 'owner-1',
        name: 'data-platform',
        displayName: 'Data Platform Team',
        type: 'team',
      },
    ],
    service: {
      id: 'svc-s3',
      type: 'storageService',
      name: 's3',
      displayName: 'S3',
    },
    numberOfObjects: 1523,
    version: 1.0,
    updatedAt: Date.now() - 3600000,
    updatedBy: 'system',
  },
} as unknown as Record<string, MockAssetDetail>;

const MOCK_TOPICS = {
  'kafka.user_events': {
    id: 'topic-1',
    name: 'user_events',
    displayName: 'User Events',
    fullyQualifiedName: 'kafka.user_events',
    description: '사용자 행동 이벤트를 실시간으로 스트리밍하는 Kafka 토픽입니다.',
    tags: [
      {
        tagFQN: 'Tier.Bronze',
        name: 'Bronze',
        displayName: 'Bronze',
        labelType: 'Manual',
        source: 'Classification',
        state: 'Confirmed',
      },
    ],
    owners: [
      {
        id: 'owner-4',
        name: 'streaming-team',
        displayName: 'Streaming Team',
        type: 'team',
      },
    ],
    service: {
      id: 'svc-kafka',
      type: 'messagingService',
      name: 'kafka',
      displayName: 'Kafka',
    },
    partitions: 12,
    replicationFactor: 3,
    cleanupPolicies: ['delete'],
    maximumMessageSize: 1048576,
    messageSchema: {
      schemaType: 'Avro',
    },
    version: 1.0,
    updatedAt: Date.now() - 7200000,
    updatedBy: 'admin',
  },
} as unknown as Record<string, MockAssetDetail>;

const inferDataType = (columnName: string): { dataType: string; dataTypeDisplay: string } => {
  const lowerName = columnName.toLowerCase();

  if (lowerName.endsWith('_at') || lowerName.includes('time') || lowerName === 'date') {
    if (lowerName === 'date') {
      return { dataType: 'DATE', dataTypeDisplay: 'date' };
    }
    return { dataType: 'TIMESTAMP', dataTypeDisplay: 'timestamp' };
  }

  if (
    lowerName.endsWith('_id') ||
    lowerName === 'id' ||
    lowerName.includes('uuid') ||
    lowerName.includes('code') ||
    lowerName.includes('name') ||
    lowerName.includes('description') ||
    lowerName.includes('type') ||
    lowerName.includes('status') ||
    lowerName.includes('category') ||
    lowerName.includes('unit') ||
    lowerName.includes('enterprise') ||
    lowerName === 'site' ||
    lowerName === 'area' ||
    lowerName === 'process' ||
    lowerName === 'component' ||
    lowerName.includes('sensitivity')
  ) {
    return { dataType: 'STRING', dataTypeDisplay: 'string' };
  }

  if (
    lowerName.includes('count') ||
    lowerName.includes('size') ||
    lowerName === 'rpm' ||
    lowerName.includes('sampling_rate') ||
    lowerName.includes('sampling_size')
  ) {
    return { dataType: 'LONG', dataTypeDisplay: 'long' };
  }

  if (
    lowerName.includes('ratio') ||
    lowerName.includes('factor') ||
    lowerName.includes('amplitude') ||
    lowerName.includes('frequency') ||
    lowerName.includes('bandwidth') ||
    lowerName.includes('value') ||
    lowerName.includes('peak')
  ) {
    return { dataType: 'DOUBLE', dataTypeDisplay: 'double' };
  }

  if (lowerName.includes('data') && !lowerName.includes('_at')) {
    return { dataType: 'MAP', dataTypeDisplay: 'map<string, list<double>>' };
  }

  return { dataType: 'STRING', dataTypeDisplay: 'string' };
};

const createIcebergTableDetails = (): Record<string, MockAssetDetail> => {
  const tables: Record<string, MockAssetDetail> = {};

  ALL_ICEBERG_DATA.forEach((hit) => {
    const source = hit._source;
    const fqn = source.fullyQualifiedName;

    if (!fqn) return;

    const columns =
      source.columnNames?.map((colName, index) => {
        const { dataType, dataTypeDisplay } = inferDataType(colName);
        return {
          name: colName,
          dataType,
          dataTypeDisplay,
          description: undefined,
          fullyQualifiedName: `${fqn}.${colName}`,
          tags: [],
          constraint: undefined,
          ordinalPosition: index + 1,
        };
      }) || [];

    // terms를 glossary 형식의 tags로 변환
    const sourceWithTerms = source as any;
    const glossaryTermTags =
      sourceWithTerms.terms && Array.isArray(sourceWithTerms.terms)
        ? sourceWithTerms.terms.map((term: any) => ({
            tagFQN: term.fullyQualifiedName,
            name: term.name,
            displayName: term.displayName || term.name,
            labelType: 'Manual',
            description: term.displayName || term.name,
            source: 'Glossary',
            state: 'Confirmed',
          }))
        : [];

    // tags와 glossaryTermTags를 합침
    const allTags = [...(source.tags || []), ...glossaryTermTags];

    tables[fqn] = {
      id: hit._id,
      name: source.name,
      displayName: source.displayName || source.name,
      fullyQualifiedName: fqn,
      description: source.description,
      tags: allTags,
      terms: sourceWithTerms.terms || [],
      owners: source.owners || [],
      service: source.service,
      database: source.database,
      databaseSchema: source.databaseSchema,
      tier: source.tier,
      domains: source.domains,
      tableType: 'Regular',
      columns: columns as any,
      usageSummary: source.usageSummary,
      version: source.version || 1.0,
      updatedAt: source.updatedAt,
      updatedBy: source.updatedBy || 'system',
      certification: source.certification,
    } as MockAssetDetail;
  });

  return tables;
};

const ICEBERG_TABLES = createIcebergTableDetails();

export const getAssetDetailMockData = (fqn: string, entityType: string): MockAssetDetail | null => {
  switch (entityType) {
    case 'table':
      return (ICEBERG_TABLES[fqn] || MOCK_TABLES[fqn] || null) as any;
    case 'database':
      return (MOCK_DATABASES[fqn] || null) as any;
    case 'databaseSchema':
      return (MOCK_SCHEMAS[fqn] || null) as any;
    case 'container':
      return (MOCK_CONTAINERS[fqn] || null) as any;
    case 'topic':
      return (MOCK_TOPICS[fqn] || null) as any;
    default:
      return null;
  }
};

export const getTableColumnsMockData = (fqn: string): any => {
  const tableDetail = ICEBERG_TABLES[fqn] || MOCK_TABLES[fqn];

  if (!tableDetail || !tableDetail.columns || tableDetail.columns.length === 0) {
    return {
      data: [],
      paging: {
        total: 0,
      },
    };
  }

  return {
    data: tableDetail.columns,
    paging: {
      total: tableDetail.columns.length,
    },
  };
};
